import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Crown } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { ShareChampionButton, champMeta } from "@/components/ChampionShare";
import { MONTH_NAMES, fmtNum } from "@/lib/helpers";

const COLORS = ["#facc15", "#fbbf24", "#fde68a", "#f59e0b", "#60a5fa", "#34d399"];

function GoldRain() {
  const pieces = useMemo(() => Array.from({ length: 140 }, (_, i) => ({
    id: i, x: Math.random() * 100, delay: Math.random() * 3, dur: 3 + Math.random() * 2.5,
    rot: Math.random() * 900 - 450, size: 6 + Math.random() * 10, color: COLORS[i % COLORS.length], drift: Math.random() * 30 - 15,
  })), []);
  return pieces.map((p) => (
    <motion.span key={p.id} className="absolute top-0 rounded-sm" style={{ left: `${p.x}%`, width: p.size, height: p.size * 0.45, background: p.color }}
                 initial={{ y: -40, rotate: 0, x: 0 }} animate={{ y: "105vh", rotate: p.rot, x: `${p.drift}vw` }}
                 transition={{ duration: p.dur, delay: p.delay, ease: "easeIn", repeat: 1 }} />
  ));
}

export function ChampionCelebration({ champ, onDone }) {
  useEffect(() => {
    if (!champ) return;
    const t = setTimeout(onDone, 12000);
    return () => clearTimeout(t);
  }, [champ, onDone]);
  return createPortal(
    <AnimatePresence>
      {champ && (
        <motion.div key={`champ-${champ.kind}-${champ.year}-${champ.month}`} className="force-dark fixed inset-0 z-[90] overflow-hidden flex items-center justify-center cursor-pointer"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onDone} data-testid={champ.kind === "mgr" ? "mgr-champion-celebration" : "champion-celebration"}>
          <div className="absolute inset-0 bg-slate-950/85 backdrop-blur-sm" />
          <motion.div className="absolute h-[60vmin] w-[60vmin] rounded-full bg-yellow-400/20 blur-3xl"
                      animate={{ scale: [1, 1.25, 1] }} transition={{ duration: 3, repeat: Infinity }} />
          <GoldRain />
          <motion.div initial={{ scale: 0.3, opacity: 0, y: 60 }} animate={{ scale: [0.3, 1.1, 1], opacity: 1, y: 0 }}
                      exit={{ scale: 1.2, opacity: 0 }} transition={{ duration: 0.9, ease: [0.32, 0.72, 0, 1] }}
                      className="relative text-center px-10">
            <div className="text-sm sm:text-base font-mono uppercase tracking-[0.4em] text-yellow-200">{champ.kind === "mgr" ? `${MONTH_NAMES[champ.month - 1]} ${champ.year} · Ayın Müdürü` : `${MONTH_NAMES[champ.month - 1]} ${champ.year} Şampiyonu`}</div>
            <div className="relative mx-auto mt-20 mb-6 w-fit">
              <motion.div animate={{ rotate: [0, -8, 8, 0], y: [0, -6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}
                          className="absolute -top-14 left-1/2 -translate-x-1/2"><Crown size={64} className="text-yellow-300 drop-shadow-[0_0_24px_rgba(250,204,21,0.7)]" /></motion.div>
              <div className="rounded-[2rem] p-1.5 bg-gradient-to-br from-yellow-300 via-amber-500 to-yellow-600 shadow-[0_0_80px_rgba(250,204,21,0.45)]">
                <UserAvatar path={champ.photo_path} name={champ.name} className="h-44 w-44 text-6xl" rounded="rounded-[1.7rem]" />
              </div>
            </div>
            <div className="font-display text-5xl sm:text-7xl font-black text-white" data-testid="champion-name">{champ.name}</div>
            {champ.branch_name && <div className="text-2xl text-slate-300 mt-2">{champ.branch_name}</div>}
            <div className="mt-4 font-display text-4xl font-black text-yellow-300" data-testid="champion-qty">{fmtNum(champ.qty)} adet{champMeta(champ).mgr ? " · şube toplamı" : ""}</div>
            <ShareChampionButton champ={champ} label="Tebrik Kartını Paylaş" className="mt-6 px-6 py-2.5 text-base" testId="champion-share-btn" />
            <div className="text-xs text-slate-500 mt-4">Kapatmak için boş alana tıklayın</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
